// @flow
import * as React from 'react'
import type { Question } from 'types/question'
import type { Response } from 'types/response'
import type { SessionObject } from 'types/session-object'
import RadioField from 'ui/molecules/RadioField'
import { hasText } from 'utils/helpers'

import ZentakeFormContext from '../ZentakeFormContext'

type Props = {
  question: Question,
  isOfficeUse: boolean,
  response: Response,
  sessions?: SessionObject,
  mode?: string,
}

function VerticalRadioListQuestion({
  question,
  isOfficeUse,
  response,
  sessions,
  mode,
}: Props): React.Node {
  const [details, setDetails] = React.useState({})

  const formContext = React.useContext(ZentakeFormContext)
  const readonly = formContext.isQuestionReadonly(question)
  const willShowNotAllowedCursor =
    formContext.willShowNotAllowedCursor(question)

  const isDirty = formContext.isQuestionDirty(question)
  const isValid = formContext.isQuestionValid(question)
  const questionError = formContext.getQuestionError(question)

  const [hasError, setHasError] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState(
    'This field is required'
  )

  const officeUsePreview = mode === 'preview' && question.is_office_use_only

  React.useEffect(() => {
    setHasError(
      (isDirty && question?.is_required && !isValid) || hasText(questionError)
    )
    setErrorMessage(
      questionError && typeof questionError === 'string'
        ? questionError
        : 'This field is required'
    )
  }, [isDirty, question.is_required, isValid, questionError])

  const answer = formContext.getAnswer(question)

  function getValue(choiceId: string) {
    return (
      hasText(answer?.answer_raw) && answer.answer_raw === choiceId.toString()
    )
  }

  const getChoiceDetails = React.useCallback(
    (choiceId) => {
      if (!details?.[choiceId]) {
        return (
          answer?.answer_choices?.find(
            // eslint-disable-next-line
            (ch) => ch?.question_choice?.id == choiceId
          )?.text_details || ''
        )
      }
      return details?.[choiceId] ?? ''
    },
    [answer, details]
  )

  function handleChange(choiceId: string, textDetails: ?string) {
    !hasText(textDetails) && setDetails({})
    formContext.onAnswerChanged(question, { choiceId, textDetails })
  }

  const updateLocalDetails = React.useCallback((choiceId, value) => {
    setDetails((prevDetails) => ({
      ...prevDetails,
      [choiceId]: value,
    }))
  }, [])

  return (
    <RadioField
      choices={question.question_choices.map((choice) => ({
        id: choice.id,
        label: choice.text_value,
        requestDetails: choice.request_details,
        value: choice.id,
      }))}
      errorLabel={errorMessage}
      getDetails={getChoiceDetails}
      hasError={hasError}
      isChecked={getValue}
      isOfficeUse={isOfficeUse || officeUsePreview}
      isRequired={question.is_required}
      label={question.text}
      numberOfColumns={question.num_columns}
      onChange={handleChange}
      onChangeDetails={updateLocalDetails}
      questionId={question.id}
      readonly={readonly}
      response={response}
      sessions={sessions}
      willShowNotAllowedCursor={willShowNotAllowedCursor}
    />
  )
}

export default VerticalRadioListQuestion
